import { useLingui } from '@lingui/react/macro'
import { Box } from 'designSystem/components/Box'
import { Text } from 'designSystem/components/Text'
import {
  ToggleGroup,
  ToggleGroupItem,
} from 'designSystem/components/ToggleGroup'
import React, { useCallback } from 'react'
import { z } from 'zod'

export type ShouldHaveMetadataFieldProps = {
  value: boolean | undefined
  disabled: boolean
  onChange: (value: boolean) => void
}

const shouldHaveMetadataToggleSchema = z.enum(['yes', 'no'])
type ShouldHaveMetadataToggleValue = z.infer<
  typeof shouldHaveMetadataToggleSchema
>

function shouldHaveMetadataToggleValue(
  value: boolean | undefined
): ShouldHaveMetadataToggleValue | '' {
  if (value === true) {
    return 'yes'
  }
  if (value === false) {
    return 'no'
  }
  return ''
}

export function ShouldHaveMetadataField({
  value,
  disabled,
  onChange,
}: ShouldHaveMetadataFieldProps): React.ReactElement {
  const { t } = useLingui()
  const handleValueChange = useCallback(
    (nextValue: string) => {
      const parsed = shouldHaveMetadataToggleSchema.safeParse(nextValue)
      if (!parsed.success) return
      onChange(parsed.data === 'yes')
    },
    [onChange]
  )

  return (
    <Box display='flex' direction='column' gap='sm'>
      <Text variant='body' weight='medium'>
        {t`Should this page have metadata?`}
      </Text>
      <Text variant='body' color='muted'>
        {t`Pipeline values are empty. Choose no to confirm that, or fill the fields if it should have metadata.`}
      </Text>
      <ToggleGroup
        type='single'
        variant='outline'
        size='lg'
        value={shouldHaveMetadataToggleValue(value)}
        disabled={disabled}
        onValueChange={handleValueChange}
        aria-label={t`Should this page have metadata?`}>
        <ToggleGroupItem value='yes'>{t`Yes`}</ToggleGroupItem>
        <ToggleGroupItem value='no'>{t`No`}</ToggleGroupItem>
      </ToggleGroup>
    </Box>
  )
}
